import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import Logo from '@/components/feature/Logo';
import { defaultProfileConfig } from '@/mocks/adminRadioProfile';
import { adminPrograms } from '@/mocks/adminPrograms';
import { stationHosts } from '@/mocks/adminHosts';
import {
  onboardingSteps,
  checklistItems,
  socialPlatformsList,
  scheduleTemplates,
  onboardingCategories,
  onboardingCountries,
  type OnboardingStep,
  type ChecklistItem,
} from '@/mocks/adminOnboarding';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_MB = 5;

interface ScheduleEntry {
  id: number;
  day: string;
  timeRange: string;
  program: string;
  host: string;
}

interface SocialEntry {
  id: string;
  url: string;
}

interface OnboardingFormData {
  radioName: string;
  slogan: string;
  description: string;
  category: string;
  country: string;
  city: string;
  language: string;
  timezone: string;
  streamUrl: string;
  streamPort: string;
  streamFormat: string;
  bitrate: string;
  autoReconnect: boolean;
  logoFile: File | null;
  logoPreview: string;
  coverFile: File | null;
  coverPreview: string;
  accentColor: string;
  socials: SocialEntry[];
  moderateEnabled: boolean;
  maxMessages: number;
  wordFilter: boolean;
  blockedWords: string;
  allowAnonymous: boolean;
  waitTime: number;
  maxChars: number;
  showListeners: boolean;
  scheduleEntries: ScheduleEntry[];
}

const initialFormData: OnboardingFormData = {
  radioName: '',
  slogan: '',
  description: '',
  category: '',
  country: '',
  city: '',
  language: 'Español',
  timezone: 'GMT-6',
  streamUrl: '',
  streamPort: '8000',
  streamFormat: 'MP3',
  bitrate: '128',
  autoReconnect: true,
  logoFile: null,
  logoPreview: '',
  coverFile: null,
  coverPreview: '',
  accentColor: '#f97316',
  socials: socialPlatformsList.map((p) => ({ id: p.id, url: '' })),
  moderateEnabled: true,
  maxMessages: 5,
  wordFilter: true,
  blockedWords: '',
  allowAnonymous: false,
  waitTime: 30,
  maxChars: 500,
  showListeners: true,
  scheduleEntries: scheduleTemplates.map((t, i) => ({
    id: i + 1,
    day: t.day,
    timeRange: t.timeRange,
    program: '',
    host: '',
  })),
};

const brandingColors = [
  { value: '#ef4444', label: 'Rojo' },
  { value: '#f97316', label: 'Naranja' },
  { value: '#eab308', label: 'Amarillo' },
  { value: '#22c55e', label: 'Verde' },
  { value: '#14b8a6', label: 'Turquesa' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#f43f5e', label: 'Fucsia' },
  { value: '#a855f7', label: 'Violeta' },
  { value: '#78716c', label: 'Café' },
  { value: '#facc15', label: 'Dorado' },
];

function computeChecklistProgress(form: OnboardingFormData): Record<string, boolean> {
  return {
    stream: form.streamUrl.trim().length > 0,
    logo: form.logoPreview.length > 0,
    profile: form.radioName.trim().length > 0 && form.category.length > 0 && form.country.length > 0,
    programs: adminPrograms.filter((p) => p.isActive).length > 0,
    hosts: stationHosts.filter((h) => h.status === 'active' || h.status === 'on_air').length > 0,
    social: form.socials.filter((s) => s.url.trim().length > 0).length > 0,
    domain: defaultProfileConfig.webUrl.trim().length > 0,
    posts: false,
    cabina: form.moderateEnabled,
  };
}

export default function AdminOnboarding() {
  const navigate = useNavigate();
  const { currentPlan, isFree, isPro, isPremium } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<OnboardingFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [isDraggingCover, setIsDraggingCover] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [previewLogo, setPreviewLogo] = useState(false);
  const [previewCover, setPreviewCover] = useState(false);

  const isPaid = isPro || isPremium;
  const stepCount = onboardingSteps.length;
  const progressPercent = ((currentStep - 1) / (stepCount - 1)) * 100;
  const checks = computeChecklistProgress(formData);
  const completedCheckCount = Object.values(checks).filter(Boolean).length;
  const totalCheckCount = checklistItems.length;
  const allChecksDone = completedCheckCount === totalCheckCount;

  const updateField = <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSocialUrl = (platformId: string, url: string) => {
    setFormData((prev) => ({
      ...prev,
      socials: prev.socials.map((s) => (s.id === platformId ? { ...s, url } : s)),
    }));
  };

  const handleImageFile = (
    file: File,
    type: 'logo' | 'cover',
  ) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setErrors((prev) => ({ ...prev, [type]: 'Formato no soportado. Usa JPG, PNG o WebP.' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, [type]: `La imagen debe pesar menos de ${MAX_FILE_SIZE_MB} MB.` }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (type === 'logo') {
        setFormData((prev) => ({ ...prev, logoFile: file, logoPreview: dataUrl }));
      } else {
        setFormData((prev) => ({ ...prev, coverFile: file, coverPreview: dataUrl }));
      }
      if (errors[type]) {
        setErrors((prev) => { const n = { ...prev }; delete n[type]; return n; });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent, type: 'logo' | 'cover') => {
    e.preventDefault();
    if (type === 'logo') setIsDraggingLogo(false);
    else setIsDraggingCover(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageFile(file, type);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'cover') => {
    const file = e.target.files?.[0];
    if (file) handleImageFile(file, type);
    if (type === 'logo' && logoInputRef.current) logoInputRef.current.value = '';
    if (type === 'cover' && coverInputRef.current) coverInputRef.current.value = '';
  };

  const removeImage = (type: 'logo' | 'cover') => {
    if (type === 'logo') {
      setFormData((prev) => ({ ...prev, logoFile: null, logoPreview: '' }));
    } else {
      setFormData((prev) => ({ ...prev, coverFile: null, coverPreview: '' }));
    }
  };

  const addScheduleEntry = () => {
    const newId = Math.max(0, ...formData.scheduleEntries.map((s) => s.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      scheduleEntries: [...prev.scheduleEntries, { id: newId, day: '', timeRange: '', program: '', host: '' }],
    }));
  };

  const removeScheduleEntry = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      scheduleEntries: prev.scheduleEntries.filter((s) => s.id !== id),
    }));
  };

  const updateScheduleEntry = (id: number, field: keyof ScheduleEntry, value: string) => {
    setFormData((prev) => ({
      ...prev,
      scheduleEntries: prev.scheduleEntries.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.radioName.trim()) newErrors.radioName = 'El nombre de la radio es obligatorio';
      if (!formData.category) newErrors.category = 'Selecciona una categoría';
      if (!formData.country) newErrors.country = 'Selecciona un país';
    }

    if (step === 2) {
      if (!formData.streamUrl.trim()) newErrors.streamUrl = 'La URL del stream es obligatoria';
      else if (!/^https?:\/\/.+/.test(formData.streamUrl.trim())) newErrors.streamUrl = 'Ingresa una URL válida (http:// o https://)';
    }

    if (step === 5) {
      if (formData.maxMessages < 1) newErrors.maxMessages = 'Mínimo 1 mensaje';
      if (formData.waitTime < 5) newErrors.waitTime = 'Mínimo 5 segundos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < stepCount) setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handlePublish = () => {
    setShowSuccess(true);
  };

  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const currentStepData = onboardingSteps[currentStep - 1];

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <Logo size="sm" />
              <h1 className="text-foreground-950 font-bold text-sm">Onboarding</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center px-4 py-16">
            <div className="w-full max-w-lg bg-background-50 rounded-2xl border border-background-200 p-8 md:p-10 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                <i className="ri-check-line text-3xl text-green-600 font-bold"></i>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                ¡Tu radio está lista!
              </h2>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
                isFree
                  ? 'bg-amber-50 border border-amber-200 text-amber-700'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}>
                <i className={isFree ? 'ri-time-line' : 'ri-flashlight-line'}></i>
                {isFree ? 'Sujeto a aprobación' : 'Activación inmediata'}
              </div>
              <p className="text-foreground-500 text-sm leading-relaxed mb-2">
                {formData.radioName || 'Tu radio'} ya está configurada.
              </p>
              <p className="text-foreground-400 text-xs leading-relaxed mb-8">
                {isFree
                  ? 'Nuestro equipo revisará tu estación en las próximas 24 a 48 horas. Te notificaremos cuando esté aprobada.'
                  : 'Tu radio ya está transmitiendo. Entrá al panel y seguí configurando.'}
              </p>
              {isFree && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mb-8">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    <i className="ri-information-line mr-1"></i>
                    Asegurate de que tu stream esté funcionando para acelerar la aprobación.
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/admin/dashboard"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                >
                  Ir al panel de administración
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-8 py-3 bg-background-50 hover:bg-background-100 text-foreground-700 text-sm font-semibold rounded-xl border border-background-200 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-3-line mr-2"></i>
                  Volver al inicio
                </Link>
              </div>
            </div>
          </main>
        </div>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
          <div className="flex items-center justify-around h-[56px] px-1">
            {[
              { icon: 'ri-dashboard-line', label: 'Dashboard', active: true, href: '/admin/dashboard' },
              { icon: 'ri-file-list-3-line', label: 'Contenido', active: false, href: '/admin/posts' },
              { icon: 'ri-bar-chart-2-line', label: 'Estadísticas', active: false, href: '/admin/analytics' },
              { icon: 'ri-broadcast-line', label: 'Cabina', active: false, href: '/admin/cabina' },
              { icon: 'ri-settings-3-line', label: 'Ajustes', active: false, href: '/admin/settings' },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer transition-colors relative ${
                  item.active ? 'text-primary-600' : 'text-foreground-400'
                }`}
              >
                <i className={`${item.icon} text-xl`}></i>
                <span className="text-[10px] font-semibold">{item.label}</span>
                {item.active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-500 rounded-full"></span>
                )}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <Logo size="sm" />
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Onboarding</h1>
              <p className="text-foreground-400 text-[9px]">Paso {currentStep} de {stepCount}</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            onClick={async (e) => {
              e.preventDefault();
              const result = await Swal.fire({
                title: '¿Saltar onboarding?',
                html: '<p class="text-sm text-foreground-500">Perderás todo el progreso de configuración. Podés volver a completarlo después desde el Dashboard.</p>',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Saltar y perder progreso',
                cancelButtonText: 'Seguir configurando',
                confirmButtonColor: '#9ca3af',
                cancelButtonColor: '#f59e0b',
                customClass: {
                  popup: 'rounded-2xl',
                  title: 'font-bold text-sm',
                  confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                  cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                  htmlContainer: 'text-sm',
                },
                buttonsStyling: false,
              });
              if (result.isConfirmed) {
                navigate('/admin/dashboard');
              }
            }}
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors cursor-pointer"
          >
            <i className="ri-skip-forward-line text-xs"></i>
            Saltar
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Configuración inicial</h2>
              <p className="text-foreground-400 text-xs">
                Completá estos pasos para dejar tu radio lista. Paso {currentStep} de {stepCount}.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Plan badge */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                isFree
                  ? 'bg-secondary-50 border border-secondary-200'
                  : isPro
                    ? 'bg-primary-50 border border-primary-200'
                    : 'bg-accent-50 border border-accent-200'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  isFree ? 'bg-secondary-500' : isPro ? 'bg-primary-500' : 'bg-accent-500'
                }`}></span>
                <span className={`text-xs font-bold ${
                  isFree ? 'text-secondary-600' : isPro ? 'text-primary-600' : 'text-accent-600'
                }`}>
                  Plan {currentPlan.name}
                </span>
              </div>
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  const result = await Swal.fire({
                    title: '¿Saltar onboarding?',
                    html: '<p class="text-sm text-foreground-500">Perderás todo el progreso de configuración. Podés volver a completarlo después desde el Dashboard.</p>',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Saltar y perder progreso',
                    cancelButtonText: 'Seguir configurando',
                    confirmButtonColor: '#9ca3af',
                    cancelButtonColor: '#f59e0b',
                    customClass: {
                      popup: 'rounded-2xl',
                      title: 'font-bold text-sm',
                      confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                      cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                      htmlContainer: 'text-sm',
                    },
                    buttonsStyling: false,
                  });
                  if (result.isConfirmed) {
                    navigate('/admin/dashboard');
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                Saltar onboarding
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8">
            {/* Plan context banner */}
            <div className={`mb-6 rounded-xl p-4 border ${
              isFree
                ? 'bg-amber-50/70 border-amber-200'
                : 'bg-green-50/70 border-green-200'
            }`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                  isFree ? 'bg-amber-200' : 'bg-green-200'
                }`}>
                  <i className={`text-sm ${isFree ? 'ri-time-line text-amber-700' : 'ri-flashlight-line text-green-700'}`}></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground-800 mb-0.5">
                    {isFree ? 'Tu radio está sujeta a aprobación' : 'Tu radio puede activarse inmediatamente'}
                  </p>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {isFree
                      ? 'En el plan Free, tu radio será revisada por nuestro equipo antes de publicarse. Completá todos los pasos del checklist para acelerar el proceso (24-48 hs).'
                      : 'Con tu plan, la radio se publica al instante. Aún así, te recomendamos completar todos los pasos para que tu perfil sea atractivo para los oyentes.'}
                  </p>
                </div>
                {isFree && (
                  <button
                    onClick={openUpgradeModal}
                    className="px-3 py-1.5 rounded-lg bg-secondary-500 text-background-50 text-[10px] font-bold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer shrink-0"
                  >
                    Mejorar plan
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main wizard area */}
              <div className="flex-1 min-w-0">
                {/* Progress stepper */}
                <div className="mb-6">
                  <div className="flex items-center gap-0.5">
                    {onboardingSteps.map((step, idx) => {
                      const status = getStepStatus(step.id);
                      const isLast = idx === onboardingSteps.length - 1;
                      return (
                        <div key={step.id} className="flex items-center flex-1 min-w-0">
                          <button
                            onClick={() => {
                              if (step.id < currentStep) setCurrentStep(step.id);
                            }}
                            className={`relative flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-all duration-300 ${
                              step.id < currentStep ? 'cursor-pointer' : 'cursor-default'
                            } ${
                              status === 'completed'
                                ? 'bg-primary-500 text-background-50'
                                : status === 'current'
                                ? 'bg-primary-500 text-background-50 ring-4 ring-primary-200 scale-110'
                                : 'bg-background-200 text-foreground-400'
                            }`}
                            title={step.label}
                          >
                            {status === 'completed' ? (
                              <i className="ri-check-line text-sm font-bold"></i>
                            ) : (
                              <span className="text-xs font-bold">{step.id}</span>
                            )}
                          </button>
                          {!isLast && (
                            <div className={`flex-1 h-1 mx-1 rounded-full transition-colors ${
                              status === 'completed' ? 'bg-primary-500' : 'bg-background-200'
                            }`}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="hidden md:flex items-center justify-between mt-2 px-0">
                    {onboardingSteps.map((step) => {
                      const status = getStepStatus(step.id);
                      return (
                        <span
                          key={step.id}
                          className={`text-[10px] font-medium text-center transition-colors ${
                            status === 'current' ? 'text-primary-600 font-semibold' : status === 'completed' ? 'text-primary-500' : 'text-foreground-400'
                          }`}
                          style={{ width: `${100 / onboardingSteps.length}%` }}
                        >
                          {step.label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Step card */}
                <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                  {/* Step header */}
                  <div className="px-5 md:px-8 py-5 border-b border-background-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                      <i className={`${currentStepData?.icon} text-primary-600 text-base`}></i>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider">
                        Paso {currentStep} de {stepCount}
                      </p>
                      <h3 className="text-lg font-bold text-foreground-950">{currentStepData?.label}</h3>
                      <p className="text-[11px] text-foreground-400 mt-0.5">{currentStepData?.description}</p>
                    </div>
                  </div>

                  {/* Step body */}
                  <div className="px-5 md:px-8 py-6">
                    {/* ── STEP 1: Datos básicos ── */}
                    {currentStep === 1 && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                            Nombre de la radio <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.radioName}
                            onChange={(e) => updateField('radioName', e.target.value)}
                            placeholder="Ej: Radio Nueva Actitud"
                            maxLength={80}
                            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 transition-all ${
                              errors.radioName
                                ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                : 'border-background-200 bg-background-50 focus:ring-primary-400/60'
                            }`}
                          />
                          {errors.radioName && <p className="text-[11px] text-red-500 mt-1">{errors.radioName}</p>}
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Eslogan</label>
                          <input
                            type="text"
                            value={formData.slogan}
                            onChange={(e) => updateField('slogan', e.target.value)}
                            placeholder="Ej: La que suena diferente"
                            maxLength={120}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Descripción</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => updateField('description', e.target.value)}
                            placeholder="Contanos de qué trata tu radio..."
                            maxLength={500}
                            rows={3}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none"
                          ></textarea>
                          <p className="text-[10px] text-foreground-400 mt-1 text-right">{formData.description.length}/500</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                              Categoría <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.category}
                              onChange={(e) => updateField('category', e.target.value)}
                              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all appearance-none bg-background-50 ${
                                errors.category
                                  ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                  : 'border-background-200 focus:ring-primary-400/60'
                              }`}
                            >
                              <option value="">Selecciona una categoría</option>
                              {onboardingCategories.map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                            {errors.category && <p className="text-[11px] text-red-500 mt-1">{errors.category}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                              País <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={formData.country}
                              onChange={(e) => updateField('country', e.target.value)}
                              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all appearance-none bg-background-50 ${
                                errors.country
                                  ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                  : 'border-background-200 focus:ring-primary-400/60'
                              }`}
                            >
                              <option value="">Selecciona un país</option>
                              {onboardingCountries.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                            {errors.country && <p className="text-[11px] text-red-500 mt-1">{errors.country}</p>}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Ciudad</label>
                            <input
                              type="text"
                              value={formData.city}
                              onChange={(e) => updateField('city', e.target.value)}
                              placeholder="Ej: Buenos Aires"
                              maxLength={60}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Idioma</label>
                            <select
                              value={formData.language}
                              onChange={(e) => updateField('language', e.target.value)}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all appearance-none"
                            >
                              <option value="Español">Español</option>
                              <option value="Inglés">Inglés</option>
                              <option value="Portugués">Portugués</option>
                              <option value="Bilingüe">Bilingüe</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Zona horaria</label>
                            <select
                              value={formData.timezone}
                              onChange={(e) => updateField('timezone', e.target.value)}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all appearance-none"
                            >
                              <option value="GMT-8">GMT-8 (Los Ángeles)</option>
                              <option value="GMT-7">GMT-7 (Denver)</option>
                              <option value="GMT-6">GMT-6 (México, Chicago)</option>
                              <option value="GMT-5">GMT-5 (Bogotá, Lima, Nueva York)</option>
                              <option value="GMT-4">GMT-4 (Santiago, Caracas)</option>
                              <option value="GMT-3">GMT-3 (Buenos Aires, São Paulo)</option>
                              <option value="GMT+0">GMT+0 (Londres)</option>
                              <option value="GMT+1">GMT+1 (Madrid, París)</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 2: Stream URL ── */}
                    {currentStep === 2 && (
                      <div className="space-y-4">
                        <div className={`rounded-xl p-4 border ${checks.stream ? 'bg-green-50/70 border-green-200' : 'bg-background-100/70 border-background-200'}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${checks.stream ? 'bg-green-200' : 'bg-secondary-100'}`}>
                              <i className={`text-sm ${checks.stream ? 'ri-check-line text-green-600' : 'ri-information-line text-secondary-600'}`}></i>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground-800 mb-1">
                                {checks.stream ? 'Stream configurado ✓' : 'Configurá tu stream'}
                              </p>
                              <p className="text-xs text-foreground-500 leading-relaxed">
                                Ingresá la URL de tu servidor de streaming. Si ya tenés tu propio servidor, pegá la URL pública. También podés configurar el puerto, formato y calidad de audio.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                            URL del Stream <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="url"
                            value={formData.streamUrl}
                            onChange={(e) => updateField('streamUrl', e.target.value)}
                            placeholder="https://tu-servidor.com:8000/stream"
                            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 transition-all ${
                              errors.streamUrl
                                ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                : 'border-background-200 bg-background-50 focus:ring-primary-400/60'
                            }`}
                          />
                          {errors.streamUrl && <p className="text-[11px] text-red-500 mt-1">{errors.streamUrl}</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Puerto</label>
                            <input
                              type="text"
                              value={formData.streamPort}
                              onChange={(e) => updateField('streamPort', e.target.value)}
                              placeholder="8000"
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Formato</label>
                            <select
                              value={formData.streamFormat}
                              onChange={(e) => updateField('streamFormat', e.target.value)}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all appearance-none"
                            >
                              <option value="MP3">MP3</option>
                              <option value="AAC">AAC</option>
                              <option value="AAC+">AAC+</option>
                              <option value="OGG">OGG</option>
                              <option value="FLAC">FLAC</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Bitrate</label>
                            <select
                              value={formData.bitrate}
                              onChange={(e) => updateField('bitrate', e.target.value)}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all appearance-none"
                            >
                              <option value="64">64 kbps</option>
                              <option value="96">96 kbps</option>
                              <option value="128">128 kbps</option>
                              <option value="192">192 kbps</option>
                              <option value="256">256 kbps</option>
                              <option value="320">320 kbps</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl px-4 py-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.autoReconnect}
                              onChange={(e) => updateField('autoReconnect', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                          <div>
                            <p className="text-xs font-semibold text-foreground-800">Reconexión automática</p>
                            <p className="text-[10px] text-foreground-400">Si el stream se cae, intenta reconectar automáticamente</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 3: Logo y portada ── */}
                    {currentStep === 3 && (
                      <div className="space-y-6">
                        {/* Logo upload */}
                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Logo de la radio</label>
                          <p className="text-[10px] text-foreground-400 mb-3">Recomendado: 512×512 px, fondo transparente, formato PNG o WebP.</p>

                          {!formData.logoPreview ? (
                            <div
                              onDragOver={(e) => { e.preventDefault(); setIsDraggingLogo(true); }}
                              onDragLeave={() => setIsDraggingLogo(false)}
                              onDrop={(e) => handleDrop(e, 'logo')}
                              onClick={() => logoInputRef.current?.click()}
                              className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                                isDraggingLogo
                                  ? 'border-primary-400 bg-primary-50/60 scale-[1.01]'
                                  : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
                              }`}
                            >
                              <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleFileInput(e, 'logo')}
                                className="hidden"
                              />
                              <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-2">
                                <i className="ri-image-add-line text-2xl text-foreground-400"></i>
                              </div>
                              <p className="text-sm font-semibold text-foreground-700">Arrastrá tu logo o hacé clic</p>
                              <p className="text-[11px] text-foreground-400 mt-0.5">JPG, PNG, WebP — máximo {MAX_FILE_SIZE_MB} MB</p>
                            </div>
                          ) : (
                            <div className="rounded-xl border border-background-200 bg-background-100 overflow-hidden">
                              <div className="p-4 flex items-center justify-center bg-background-100">
                                <img src={formData.logoPreview} alt="Logo preview" className="w-24 h-24 object-contain rounded-xl bg-background-50" />
                              </div>
                              <div className="flex items-center gap-2 px-4 py-3 bg-background-50 border-t border-background-200">
                                <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                                  <i className="ri-check-line text-green-600 text-xs font-bold"></i>
                                </div>
                                <span className="text-xs font-medium text-foreground-700 truncate flex-1">
                                  {formData.logoFile?.name || 'Logo cargado'}
                                </span>
                                <button
                                  onClick={() => removeImage('logo')}
                                  className="px-3 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                                >
                                  Quitar
                                </button>
                              </div>
                            </div>
                          )}
                          {errors.logo && <p className="text-[11px] text-red-500 mt-1">{errors.logo}</p>}
                        </div>

                        {/* Cover upload */}
                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Imagen de portada</label>
                          <p className="text-[10px] text-foreground-400 mb-3">Recomendado: 1600×400 px, formato JPG o WebP. Se muestra en la parte superior de tu perfil.</p>

                          {!formData.coverPreview ? (
                            <div
                              onDragOver={(e) => { e.preventDefault(); setIsDraggingCover(true); }}
                              onDragLeave={() => setIsDraggingCover(false)}
                              onDrop={(e) => handleDrop(e, 'cover')}
                              onClick={() => coverInputRef.current?.click()}
                              className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                                isDraggingCover
                                  ? 'border-primary-400 bg-primary-50/60 scale-[1.01]'
                                  : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
                              }`}
                            >
                              <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleFileInput(e, 'cover')}
                                className="hidden"
                              />
                              <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-2">
                                <i className="ri-landscape-line text-2xl text-foreground-400"></i>
                              </div>
                              <p className="text-sm font-semibold text-foreground-700">Arrastrá tu portada o hacé clic</p>
                              <p className="text-[11px] text-foreground-400 mt-0.5">JPG, PNG, WebP — máximo {MAX_FILE_SIZE_MB} MB</p>
                            </div>
                          ) : (
                            <div className="rounded-xl border border-background-200 bg-background-100 overflow-hidden">
                              <div className="p-3 flex items-center justify-center bg-background-100">
                                <img src={formData.coverPreview} alt="Cover preview" className="w-full h-28 object-cover rounded-lg" />
                              </div>
                              <div className="flex items-center gap-2 px-4 py-3 bg-background-50 border-t border-background-200">
                                <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                                  <i className="ri-check-line text-green-600 text-xs font-bold"></i>
                                </div>
                                <span className="text-xs font-medium text-foreground-700 truncate flex-1">
                                  {formData.coverFile?.name || 'Portada cargada'}
                                </span>
                                <button
                                  onClick={() => removeImage('cover')}
                                  className="px-3 py-1.5 text-[11px] font-semibold text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors whitespace-nowrap"
                                >
                                  Quitar
                                </button>
                              </div>
                            </div>
                          )}
                          {errors.cover && <p className="text-[11px] text-red-500 mt-1">{errors.cover}</p>}
                        </div>

                        {/* Color accent */}
                        <div>
                          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Color principal</label>
                          <p className="text-[10px] text-foreground-400 mb-3">Define la identidad visual de tu radio. Se usa en badges, botones y acentos.</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            {brandingColors.map((c) => (
                              <button
                                key={c.value}
                                type="button"
                                onClick={() => updateField('accentColor', c.value)}
                                className="relative cursor-pointer group"
                                title={c.label}
                              >
                                <div
                                  className={`w-9 h-9 rounded-full transition-all duration-200 ${
                                    formData.accentColor === c.value
                                      ? 'ring-2 ring-offset-2 ring-foreground-800 scale-110'
                                      : 'hover:scale-105'
                                  }`}
                                  style={{ backgroundColor: c.value }}
                                ></div>
                                {formData.accentColor === c.value && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <i className="ri-check-line text-background-50 text-xs font-bold drop-shadow-sm"></i>
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── STEP 4: Redes sociales ── */}
                    {currentStep === 4 && (
                      <div className="space-y-1">
                        <p className="text-xs text-foreground-500 mb-4">
                          Conectá tus redes sociales para que los oyentes te encuentren en todas partes. Dejalas en blanco si no querés mostrarlas.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {formData.socials.map((social) => {
                            const platform = socialPlatformsList.find((p) => p.id === social.id);
                            if (!platform) return null;
                            return (
                              <div key={social.id} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl px-4 py-3">
                                <div className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                                  <i className={`${platform.icon} text-foreground-500 text-base`}></i>
                                </div>
                                <input
                                  type="url"
                                  value={social.url}
                                  onChange={(e) => handleSocialUrl(social.id, e.target.value)}
                                  placeholder={platform.placeholder}
                                  className="flex-1 text-xs text-foreground-900 placeholder:text-foreground-300 bg-transparent focus:outline-none"
                                />
                                {social.url.trim() && (
                                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                    <i className="ri-check-line text-green-600 text-[10px] font-bold"></i>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-foreground-400 mt-3 text-right">
                          {formData.socials.filter((s) => s.url.trim()).length} de {socialPlatformsList.length} redes configuradas
                        </p>
                      </div>
                    )}

                    {/* ── STEP 5: Cabina ── */}
                    {currentStep === 5 && (
                      <div className="space-y-5">
                        {/* Moderation toggle */}
                        <div className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
                          <div>
                            <p className="text-sm font-semibold text-foreground-800">Activar moderación</p>
                            <p className="text-[11px] text-foreground-400">Filtrá mensajes ofensivos y controlá el chat</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.moderateEnabled}
                              onChange={(e) => updateField('moderateEnabled', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-10 h-6 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>

                        {formData.moderateEnabled && (
                          <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                                  Máx. mensajes por usuario
                                </label>
                                <input
                                  type="number"
                                  value={formData.maxMessages}
                                  onChange={(e) => updateField('maxMessages', parseInt(e.target.value) || 0)}
                                  min={1}
                                  max={50}
                                  className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${
                                    errors.maxMessages
                                      ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                      : 'border-background-200 bg-background-50 focus:ring-primary-400/60'
                                  }`}
                                />
                                {errors.maxMessages && <p className="text-[11px] text-red-500 mt-1">{errors.maxMessages}</p>}
                                <p className="text-[10px] text-foreground-400 mt-1">Cada cuántos segundos puede enviar un mensaje</p>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                                  Tiempo de espera (seg)
                                </label>
                                <input
                                  type="number"
                                  value={formData.waitTime}
                                  onChange={(e) => updateField('waitTime', parseInt(e.target.value) || 0)}
                                  min={5}
                                  max={300}
                                  className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${
                                    errors.waitTime
                                      ? 'border-red-300 bg-red-50/30 focus:ring-red-400/60'
                                      : 'border-background-200 bg-background-50 focus:ring-primary-400/60'
                                  }`}
                                />
                                {errors.waitTime && <p className="text-[11px] text-red-500 mt-1">{errors.waitTime}</p>}
                                <p className="text-[10px] text-foreground-400 mt-1">Tiempo mínimo entre mensajes de un mismo oyente</p>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Máx. caracteres por mensaje</label>
                              <select
                                value={formData.maxChars}
                                onChange={(e) => updateField('maxChars', parseInt(e.target.value))}
                                className="w-full sm:w-48 px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all appearance-none"
                              >
                                <option value={200}>200 caracteres</option>
                                <option value={300}>300 caracteres</option>
                                <option value={500}>500 caracteres</option>
                                <option value={1000}>1000 caracteres</option>
                              </select>
                            </div>

                            {/* Word filter */}
                            <div className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
                              <div>
                                <p className="text-sm font-semibold text-foreground-800">Filtro de palabras ofensivas</p>
                                <p className="text-[11px] text-foreground-400">Bloqueá automáticamente mensajes con lenguaje inapropiado</p>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.wordFilter}
                                  onChange={(e) => updateField('wordFilter', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-10 h-6 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                              </label>
                            </div>

                            {formData.wordFilter && (
                              <div>
                                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                                  Palabras bloqueadas adicionales
                                </label>
                                <textarea
                                  value={formData.blockedWords}
                                  onChange={(e) => updateField('blockedWords', e.target.value)}
                                  placeholder="Escribí palabras separadas por coma. Ej: spam, publicidad, enlace"
                                  rows={2}
                                  className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none"
                                ></textarea>
                                <p className="text-[10px] text-foreground-400 mt-1">Además del filtro automático, podés agregar palabras específicas de tu comunidad.</p>
                              </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
                                <div>
                                  <p className="text-sm font-semibold text-foreground-800">Permitir anónimos</p>
                                  <p className="text-[11px] text-foreground-400">Oyentes sin registro pueden enviar mensajes</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.allowAnonymous}
                                    onChange={(e) => updateField('allowAnonymous', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-10 h-6 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                                </label>
                              </div>
                              <div className="flex items-center justify-between bg-background-50 border border-background-200 rounded-xl px-4 py-3.5">
                                <div>
                                  <p className="text-sm font-semibold text-foreground-800">Mostrar oyentes</p>
                                  <p className="text-[11px] text-foreground-400">Mostrar cuántos oyentes están escuchando en vivo</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={formData.showListeners}
                                    onChange={(e) => updateField('showListeners', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-10 h-6 bg-background-300 peer-checked:bg-primary-500 rounded-full peer transition-colors after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                                </label>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* ── STEP 6: Programación ── */}
                    {currentStep === 6 && (
                      <div className="space-y-4">
                        <p className="text-xs text-foreground-500 mb-2">
                          Agregá los programas de tu grilla. Podés dejar los que no uses en blanco o eliminarlos.
                        </p>

                        <div className="space-y-3">
                          {formData.scheduleEntries.map((entry) => (
                            <div key={entry.id} className="bg-background-50 border border-background-200 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">
                                  Programa #{entry.id}
                                </span>
                                <button
                                  onClick={() => removeScheduleEntry(entry.id)}
                                  className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center cursor-pointer transition-colors"
                                  title="Eliminar programa"
                                >
                                  <i className="ri-close-line text-red-400 text-sm"></i>
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] font-semibold text-foreground-500 mb-1">Día</label>
                                  <input
                                    type="text"
                                    value={entry.day}
                                    onChange={(e) => updateScheduleEntry(entry.id, 'day', e.target.value)}
                                    placeholder="Ej: Lunes - Viernes"
                                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-400/60 transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-foreground-500 mb-1">Horario</label>
                                  <input
                                    type="text"
                                    value={entry.timeRange}
                                    onChange={(e) => updateScheduleEntry(entry.id, 'timeRange', e.target.value)}
                                    placeholder="Ej: 06:00 - 10:00"
                                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-400/60 transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-foreground-500 mb-1">Programa</label>
                                  <input
                                    type="text"
                                    value={entry.program}
                                    onChange={(e) => updateScheduleEntry(entry.id, 'program', e.target.value)}
                                    placeholder="Ej: Despierta con Energía"
                                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-400/60 transition-all"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] font-semibold text-foreground-500 mb-1">Locutor/a</label>
                                  <input
                                    type="text"
                                    value={entry.host}
                                    onChange={(e) => updateScheduleEntry(entry.id, 'host', e.target.value)}
                                    placeholder="Ej: Carlos Mendoza"
                                    className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-400/60 transition-all"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          onClick={addScheduleEntry}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-background-300 text-foreground-500 text-xs font-semibold hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 cursor-pointer transition-all"
                        >
                          <i className="ri-add-line"></i>
                          Agregar programa
                        </button>
                        <p className="text-[10px] text-foreground-400 text-right">
                          {formData.scheduleEntries.filter((s) => s.program.trim()).length} programas con nombre
                        </p>
                      </div>
                    )}

                    {/* ── STEP 7: Publicar radio ── */}
                    {currentStep === 7 && (
                      <div className="space-y-6">
                        <p className="text-sm text-foreground-500">
                          ¡Último paso! Revisá que todo esté listo antes de publicar tu radio.
                        </p>

                        {/* Checklist status */}
                        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                              <i className="ri-task-line text-primary-600 text-sm"></i>
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-foreground-800">Checklist de publicación</h4>
                              <p className="text-[10px] text-foreground-400">{completedCheckCount} de {totalCheckCount} completados</p>
                            </div>
                          </div>
                          <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden mb-4">
                            <div
                              className="h-full rounded-full bg-primary-500 transition-all duration-500"
                              style={{ width: `${(completedCheckCount / totalCheckCount) * 100}%` }}
                            ></div>
                          </div>
                          <div className="space-y-2">
                            {checklistItems.map((item) => {
                              const done = checks[item.id];
                              return (
                                <div
                                  key={item.id}
                                  className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                                    done ? 'bg-green-50/70 border border-green-200' : 'bg-background-100 border border-background-200'
                                  }`}
                                >
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                    done ? 'bg-green-200' : 'bg-background-200'
                                  }`}>
                                    <i className={`${item.icon} text-sm ${done ? 'text-green-600' : 'text-foreground-400'}`}></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-semibold ${done ? 'text-green-800' : 'text-foreground-500'}`}>
                                      {item.label}
                                    </p>
                                    <p className="text-[10px] text-foreground-400">{item.description}</p>
                                  </div>
                                  {done ? (
                                    <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                                  ) : (
                                    <button
                                      onClick={() => setCurrentStep(item.stepId)}
                                      className="px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-[10px] font-bold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                                    >
                                      Completar
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Summary */}
                        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                          <h4 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
                            <i className="ri-file-list-3-line text-primary-600"></i>
                            Resumen de configuración
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                            <div>
                              <p className="text-foreground-400">Nombre</p>
                              <p className="text-foreground-800 font-semibold">{formData.radioName || '—'}</p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Categoría</p>
                              <p className="text-foreground-800 font-semibold">{formData.category || '—'}</p>
                            </div>
                            <div>
                              <p className="text-foreground-400">País / Ciudad</p>
                              <p className="text-foreground-800 font-semibold">
                                {[formData.country, formData.city].filter(Boolean).join(', ') || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Stream</p>
                              <p className={`text-foreground-800 font-semibold truncate ${formData.streamUrl ? '' : 'text-red-500'}`}>
                                {formData.streamUrl || 'No configurado'}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Logo</p>
                              <p className={`font-semibold ${formData.logoPreview ? 'text-green-600' : 'text-foreground-400'}`}>
                                {formData.logoPreview ? '✓ Cargado' : '— Pendiente'}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Portada</p>
                              <p className={`font-semibold ${formData.coverPreview ? 'text-green-600' : 'text-foreground-400'}`}>
                                {formData.coverPreview ? '✓ Cargada' : '— Pendiente'}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Redes configuradas</p>
                              <p className="text-foreground-800 font-semibold">
                                {formData.socials.filter((s) => s.url.trim()).length} de {socialPlatformsList.length}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Programas</p>
                              <p className="text-foreground-800 font-semibold">
                                {formData.scheduleEntries.filter((s) => s.program.trim()).length} en grilla
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Moderación</p>
                              <p className={`font-semibold ${formData.moderateEnabled ? 'text-green-600' : 'text-foreground-400'}`}>
                                {formData.moderateEnabled ? 'Activada' : 'Desactivada'}
                              </p>
                            </div>
                            <div>
                              <p className="text-foreground-400">Plan</p>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                isFree ? 'bg-secondary-100 text-secondary-700' : isPro ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                              }`}>
                                {currentPlan.name}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Warning for incomplete checklist */}
                        {!allChecksDone && (
                          <div className="rounded-xl p-4 border bg-amber-50/70 border-amber-200">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                                <i className="ri-error-warning-line text-amber-700 text-sm"></i>
                              </div>
                              <div>
                                <p className="text-sm font-bold text-amber-800 mb-1">Faltan pasos por completar</p>
                                <p className="text-xs text-amber-600 leading-relaxed">
                                  {isFree
                                    ? 'Para acelerar la aprobación, te recomendamos completar todos los ítems del checklist. Las radios con perfil completo tienen prioridad.'
                                    : 'Aunque tu radio se activa inmediatamente, un perfil completo atrae más oyentes.'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Step navigation footer */}
                  <div className="px-5 md:px-8 py-4 border-t border-background-200 flex items-center justify-between bg-background-100/70">
                    <div>
                      {currentStep > 1 && (
                        <button
                          onClick={handleBack}
                          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-arrow-left-line mr-1.5"></i>
                          Anterior
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-foreground-400 hidden sm:block">
                        {currentStep}/{stepCount}
                      </span>
                      {currentStep === stepCount ? (
                        <button
                          onClick={handlePublish}
                          className={`px-6 py-2.5 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap ${
                            allChecksDone
                              ? 'bg-primary-500 hover:bg-primary-600 text-background-50 shadow-lg shadow-primary-500/20'
                              : 'bg-background-200 text-foreground-500'
                          }`}
                        >
                          {isFree ? 'Enviar a revisión' : 'Publicar radio'}
                          <i className="ri-rocket-line ml-2"></i>
                        </button>
                      ) : (
                        <button
                          onClick={handleNext}
                          className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-primary-500 hover:bg-primary-600 text-background-50 cursor-pointer transition-all whitespace-nowrap shadow-lg shadow-primary-500/20"
                        >
                          {currentStep === 1 ? 'Comenzar' : 'Siguiente'}
                          <i className="ri-arrow-right-line ml-1.5"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right panel: checklist on desktop */}
              <div className="hidden lg:block w-64 shrink-0">
                <div className="sticky top-24 space-y-4">
                  {/* Checklist panel */}
                  <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <h4 className="text-xs font-bold text-foreground-800 mb-3 flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-primary-600"></i>
                      Checklist
                    </h4>
                    <div className="w-full h-1.5 rounded-full bg-background-200 overflow-hidden mb-3">
                      <div
                        className="h-full rounded-full bg-primary-500 transition-all duration-500"
                        style={{ width: `${(completedCheckCount / totalCheckCount) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-[9px] text-foreground-400 mb-3">{completedCheckCount}/{totalCheckCount} completados</p>
                    <div className="space-y-1.5">
                      {checklistItems.map((item) => {
                        const done = checks[item.id];
                        return (
                          <button
                            key={item.id}
                            onClick={() => setCurrentStep(item.stepId)}
                            className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left cursor-pointer transition-colors ${
                              done
                                ? 'bg-green-50 hover:bg-green-100'
                                : 'bg-background-100 hover:bg-background-200'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                              done ? 'bg-green-200' : 'bg-background-200'
                            }`}>
                              <i className={`${item.icon} text-[10px] ${done ? 'text-green-600' : 'text-foreground-400'}`}></i>
                            </div>
                            <div className="min-w-0">
                              <p className={`text-[11px] font-semibold leading-tight ${done ? 'text-green-800' : 'text-foreground-500'}`}>
                                {item.label}
                              </p>
                              <p className="text-[9px] text-foreground-400 leading-tight truncate">{item.description}</p>
                            </div>
                            {done && <i className="ri-check-line text-green-500 text-xs ml-auto shrink-0"></i>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick tip */}
                  <div className="bg-accent-50 rounded-2xl border border-accent-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-lg bg-accent-200 flex items-center justify-center">
                        <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                      </div>
                      <p className="text-[11px] font-bold text-accent-800">Tip rápido</p>
                    </div>
                    <p className="text-[10px] text-accent-600 leading-relaxed">
                      Completar todos los pasos del checklist te asegura que tu perfil de radio sea atractivo para los oyentes desde el primer día.
                      {isFree && ' Además, acelera el proceso de aprobación.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}